import { ArrowUpFromLine, Layers, RefreshCw, X } from 'lucide-react';
import { MappingSetNotice } from '../../connectors/MappingSetNotice';
import { MiroIcon } from './IntegrationIcons';
import type { MiroConnection } from './useMiroConnection';
import type { PriorityExportRecord } from './integrationTypes';

export function MiroExportModal({
  miro,
  workspaceId,
  priorities,
  listName,
}: {
  miro: MiroConnection;
  workspaceId: string | null;
  priorities: PriorityExportRecord[];
  listName: string;
}) {
  const {
    showExportMiro,
    setShowExportMiro,
    selectedMiroTemplate,
    setSelectedMiroTemplate,
    selectedMiroBoard,
    setSelectedMiroBoard,
    miroTemplates,
    miroBoards,
    templateExportPreview,
    isLoading,
    loadTemplateExportPreview,
    handleTemplateExport,
  } = miro;

  return (
    <>
      {/* Export to Miro Modal (template-based) */}
      {showExportMiro && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[var(--bg-raised)] shadow-xl max-w-3xl w-full mx-4 max-h-[85vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-[var(--rule)] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[var(--mustard-dim)]">
                  <MiroIcon />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-[var(--text-pri)]">Export to Miro</h2>
                  <p className="text-sm text-[var(--text-ter)]">Create a visual board from your priorities</p>
                </div>
              </div>
              <button onClick={() => setShowExportMiro(false)} className="p-2 hover:bg-[var(--hover-tint)]">
                <X className="h-5 w-5 text-[var(--text-ter)]" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {/* Template Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
                  Prioritization Template
                </label>
                <select
                  value={selectedMiroTemplate}
                  onChange={(e) => {
                    setSelectedMiroTemplate(e.target.value);
                    if (e.target.value && priorities.length > 0) {
                      loadTemplateExportPreview(e.target.value);
                    }
                  }}
                  className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-yellow-500"
                >
                  <option value="">Choose a template...</option>
                  {miroTemplates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name} ({template.zoneCount} zones)
                    </option>
                  ))}
                </select>
                {selectedMiroTemplate && (
                  <div className="mt-2 text-xs text-[var(--text-ter)]">
                    {miroTemplates.find(t => t.id === selectedMiroTemplate)?.description}
                  </div>
                )}
              </div>

              {/* Target Board Selection (optional) */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
                  Target Board <span className="text-[var(--text-ter)]">(optional - creates new if empty)</span>
                </label>
                <select
                  value={selectedMiroBoard}
                  onChange={(e) => setSelectedMiroBoard(e.target.value)}
                  className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-yellow-500"
                >
                  <option value="">Create new board</option>
                  {miroBoards.map(board => (
                    <option key={board.id} value={board.id}>{board.name}</option>
                  ))}
                </select>
              </div>

              <MappingSetNotice connectorType="miro" flow="export" workspaceId={workspaceId} className="mb-4" />

              {/* Priorities Summary */}
              <div className="mb-4 p-3 bg-[var(--surface)]">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-[var(--text-sec)]">Priorities to Export</span>
                  <span className="text-lg font-bold text-[var(--mustard)]">{priorities.length}</span>
                </div>
                <p className="text-xs text-[var(--text-ter)] mt-1">
                  All priorities from "{listName}" will be exported
                </p>
              </div>

              {/* Loading State */}
              {isLoading && (
                <div className="flex items-center justify-center py-6">
                  <RefreshCw className="h-5 w-5 animate-spin text-[var(--mustard)]" />
                  <span className="ml-2 text-[var(--text-sec)]">Generating preview...</span>
                </div>
              )}

              {/* Template Export Preview */}
              {!isLoading && templateExportPreview && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-[var(--text-sec)] mb-3">Zone Placement Preview</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {templateExportPreview.zoneSummary.map(zone => (
                      <div
                        key={zone.zoneId}
                        className="p-3 border border-[var(--rule)] bg-[var(--bg-raised)]"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-[var(--text-pri)]">{zone.zoneName}</span>
                          <span className="text-lg font-bold text-[var(--mustard)]">{zone.itemCount}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Preview Items */}
                  {templateExportPreview.placements.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-xs font-medium text-[var(--text-ter)] mb-2">Item Placements</h4>
                      <div className="max-h-48 overflow-y-auto space-y-1">
                        {templateExportPreview.placements.slice(0, 10).map(item => (
                          <div key={item.priorityId} className="flex items-center gap-2 text-xs py-1">
                            <span className="px-2 py-0.5 bg-[var(--mustard-dim)] text-[var(--mustard)] rounded">
                              {item.zoneName || 'Unassigned'}
                            </span>
                            <span className="text-[var(--text-sec)] truncate flex-1">{item.title}</span>
                          </div>
                        ))}
                        {templateExportPreview.placements.length > 10 && (
                          <div className="text-xs text-[var(--text-ter)] text-center pt-1">
                            ...and {templateExportPreview.placements.length - 10} more items
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* No template selected message */}
              {!isLoading && !selectedMiroTemplate && (
                <div className="text-center py-6 text-[var(--text-ter)]">
                  <Layers className="h-10 w-10 mx-auto mb-2 text-[var(--text-ter)]" />
                  <p>Select a template to see how your priorities will be organized</p>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-[var(--rule)] flex items-center justify-between bg-[var(--surface)]">
              <span className="text-sm text-[var(--text-sec)]">
                {selectedMiroTemplate 
                  ? `${priorities.length} items → ${selectedMiroBoard ? 'existing board' : 'new board'}`
                  : 'Select a template to continue'}
              </span>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowExportMiro(false)}
                  className="px-4 py-2 text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleTemplateExport}
                  disabled={!selectedMiroTemplate || priorities.length === 0 || isLoading}
                  className="px-4 py-2 bg-[var(--mustard)] hover:bg-[var(--mustard)] text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isLoading ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <ArrowUpFromLine className="h-4 w-4" />
                  )}
                  Export to Miro
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
